// Lightweight, dependency-free mobile product gallery.
//
// Inline video slides show a preview image + play icon (reliable on all iOS).
// The fullscreen overlay renders the real <video controls> for video slides.
//
// Markup contract (see snippets/kani_image_slider.liquid):
//   <mobile-product-gallery>
//     <div data-mpg-viewport>
//       <ul data-mpg-track>
//         <!-- image slide -->
//         <li data-mpg-slide data-media-type="image" ...>
//           <button data-mpg-zoom>...</button>
//         </li>
//         <!-- video slide: preview img + play icon; data-video-src holds mp4 -->
//         <li data-mpg-slide data-media-type="video" data-video-src="..." ...>
//           <button data-mpg-zoom>...</button>
//         </li>
//       </ul>
//       <div data-mpg-dots></div>
//     </div>
//     <div data-mpg-overlay hidden>...</div>
//   </mobile-product-gallery>
if (!customElements.get('mobile-product-gallery')) {
  customElements.define(
    'mobile-product-gallery',
    class MobileProductGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-mpg-track]')
        this.dotsWrap = this.querySelector('[data-mpg-dots]')
        this.overlay = this.querySelector('[data-mpg-overlay]')
        this.overlayTrack =
          this.overlay && this.overlay.querySelector('[data-mpg-overlay-track]')
        this.overlayThumbs =
          this.overlay && this.overlay.querySelector('[data-mpg-overlay-thumbs]')
        this.overlayVideoBtn =
          this.overlay && this.overlay.querySelector('[data-mpg-overlay-video]')
        this.overlayPrev =
          this.overlay && this.overlay.querySelector('[data-mpg-overlay-prev]')
        this.overlayNext =
          this.overlay && this.overlay.querySelector('[data-mpg-overlay-next]')
        this._overlayVideoIndex = -1
        this._overlayIndex = 0
        if (!this.track) return

        this.activeIndex = 0

        // rAF-throttled so the active dot tracks the swipe in real time.
        this._rafPending = false
        this._onTrackScroll = () => {
          if (this._rafPending) return
          this._rafPending = true
          requestAnimationFrame(() => {
            this._rafPending = false
            this._syncActiveFromScroll()
          })
        }
        this.track.addEventListener('scroll', this._onTrackScroll, {
          passive: true,
        })

        this._onResize = this._debounce(
          () => this._scrollToIndex(this.activeIndex, false),
          150
        )
        window.addEventListener('resize', this._onResize)

        this._bindZoom()
        this._bindOverlay()

        this.zoomBtn = this.querySelector('[data-mpg-zoom-btn]')
        if (this.zoomBtn) {
          // touchstart fires ~50ms before click; use that window to ensure the
          // overlay is pre-built and the active image fetch is already in flight.
          this.zoomBtn.addEventListener(
            'touchstart',
            () => {
              this._buildOverlay()
              this._loadAround(this.activeIndex || 0)
            },
            { passive: true }
          )
          this.zoomBtn.addEventListener('click', (e) => {
            e.preventDefault()
            this.openZoom(this.activeIndex || 0)
          })
        }

        this.refresh()
        window.mobileProductGallery = this
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this._onResize)
        if (this._idleBuild != null) {
          if (typeof cancelIdleCallback === 'function')
            cancelIdleCallback(this._idleBuild)
          else clearTimeout(this._idleBuild)
          this._idleBuild = null
        }
        if (window.mobileProductGallery === this)
          window.mobileProductGallery = null
      }

      _debounce(fn, wait) {
        let t
        return (...args) => {
          clearTimeout(t)
          t = setTimeout(() => fn.apply(this, args), wait)
        }
      }

      // The currently browsable slides (matching active variant + source).
      visibleSlides() {
        return Array.from(
          this.track.querySelectorAll('[data-mpg-slide]')
        ).filter((s) => !s.classList.contains('hidden'))
      }

      // Public API: called after slides are shown/hidden (variant/bundle switch).
      refresh() {
        if (!this.track) return
        this._buildDots()
        this.activeIndex = 0
        this._scrollToIndex(0, false)
        this._setActiveDot(0)
        // Invalidate cached overlay; rebuild during idle so first tap is instant.
        this._overlaySig = null
        this._scheduleOverlayBuild()
      }

      _scheduleOverlayBuild() {
        const run = () => {
          this._idleBuild = null
          try {
            this._buildOverlay()
          } catch (e) {}
        }
        if (this._idleBuild != null) {
          if (typeof cancelIdleCallback === 'function')
            cancelIdleCallback(this._idleBuild)
          else clearTimeout(this._idleBuild)
        }
        if (typeof requestIdleCallback === 'function') {
          this._idleBuild = requestIdleCallback(run, { timeout: 2000 })
        } else {
          this._idleBuild = setTimeout(run, 400)
        }
      }

      // ── Pagination dots ──────────────────────────────────────────────────
      _buildDots() {
        if (!this.dotsWrap) return
        const slides = this.visibleSlides()
        this.dotsWrap.innerHTML = ''
        if (slides.length <= 1) {
          this.dotsWrap.classList.add('hidden')
          return
        }
        this.dotsWrap.classList.remove('hidden')
        slides.forEach((_, i) => {
          const dot = document.createElement('button')
          dot.type = 'button'
          dot.className = 'mpg-dot'
          dot.setAttribute('aria-label', 'Go to image ' + (i + 1))
          dot.addEventListener('click', () => this._scrollToIndex(i, true))
          this.dotsWrap.appendChild(dot)
        })
        this.dotsWrap.classList.add('mpg-dots--no-anim')
        this._setActiveDot(this.activeIndex || 0)
        requestAnimationFrame(() =>
          this.dotsWrap.classList.remove('mpg-dots--no-anim')
        )
      }

      _setActiveDot(active) {
        if (!this.dotsWrap) return
        const dots = Array.from(this.dotsWrap.children)
        const n = dots.length
        if (!n) return
        const WINDOW = 5
        let start = 0
        let end = n - 1
        if (n > WINDOW) {
          start = Math.min(Math.max(active - 2, 0), n - WINDOW)
          end = start + WINDOW - 1
        }
        dots.forEach((dot, i) => {
          dot.classList.remove('is-active', 'is-small', 'is-hidden')
          if (i < start || i > end) {
            dot.classList.add('is-hidden')
          } else if (i === active) {
            dot.classList.add('is-active')
          } else if ((i === start && start > 0) || (i === end && end < n - 1)) {
            dot.classList.add('is-small')
          }
        })
      }

      _scrollToIndex(i, smooth) {
        const slides = this.visibleSlides()
        if (!slides[i]) return
        this.track.scrollTo({
          left: slides[i].offsetLeft,
          behavior: smooth ? 'smooth' : 'auto',
        })
        this.activeIndex = i
        this._setActiveDot(i)
        this._syncSlideVideo(i)
      }

      _syncActiveFromScroll() {
        const slides = this.visibleSlides()
        if (!slides.length) return
        const center = this.track.scrollLeft + this.track.clientWidth / 2
        let best = 0
        let bestDist = Infinity
        slides.forEach((s, i) => {
          const c = s.offsetLeft + s.offsetWidth / 2
          const d = Math.abs(center - c)
          if (d < bestDist) {
            bestDist = d
            best = i
          }
        })
        this.activeIndex = best
        this._setActiveDot(best)
        this._syncSlideVideo(best)
      }

      // Play the active slide's video; pause all others.
      // No IntersectionObserver — just fire-and-forget on every slide change.
      _syncSlideVideo(activeIdx) {
        const slides = this.visibleSlides()
        slides.forEach((slide, i) => {
          const v = slide.querySelector('video')
          if (!v) return
          if (i === activeIdx) {
            v.muted = true
            v.setAttribute('muted', '')
            // Inline videos ship with preload="none" so hidden slides cost no
            // bandwidth; upgrade the active one so playback starts buffering.
            if (v.preload === 'none') v.preload = 'auto'
            const p = v.play()
            if (p && typeof p.catch === 'function')
              p.catch((err) =>
                console.warn(
                  '[MPG] play() blocked on slide',
                  i,
                  '—',
                  err.name,
                  err.message
                )
              )
          } else if (!v.paused) {
            v.pause()
          }
        })
      }

      // ── Tap-to-zoom fullscreen overlay ───────────────────────────────────
      _bindZoom() {
        this.track.addEventListener('click', (e) => {
          const trigger = e.target.closest('[data-mpg-zoom]')
          if (!trigger) return
          // Only skip if the user tapped a video that has native controls
          // (so they can use play/pause/scrubbing without opening the overlay).
          if (e.target.closest('video[controls]')) return
          const slide = trigger.closest('[data-mpg-slide]')
          const idx = this.visibleSlides().indexOf(slide)
          this.openZoom(idx < 0 ? 0 : idx)
        })
      }

      _bindOverlay() {
        if (!this.overlay) return
        const closeBtn = this.overlay.querySelector('[data-mpg-overlay-close]')
        if (closeBtn) closeBtn.addEventListener('click', () => this.closeZoom())

        if (this.overlayVideoBtn)
          this.overlayVideoBtn.addEventListener('click', () => {
            if (this._overlayVideoIndex >= 0)
              this._overlayScrollToIndex(this._overlayVideoIndex, true)
          })

        if (this.overlayPrev)
          this.overlayPrev.addEventListener('click', () =>
            this._overlayStep(-1)
          )
        if (this.overlayNext)
          this.overlayNext.addEventListener('click', () =>
            this._overlayStep(1)
          )

        document.addEventListener('keydown', (e) => {
          if (
            e.key === 'Escape' &&
            !this.overlay.classList.contains('hidden')
          )
            this.closeZoom()
        })

        if (this.overlayTrack) {
          this._ovRafPending = false
          this.overlayTrack.addEventListener(
            'scroll',
            () => {
              if (this._ovRafPending) return
              this._ovRafPending = true
              requestAnimationFrame(() => {
                this._ovRafPending = false
                this._overlaySyncFromScroll()
              })
            },
            { passive: true }
          )
        }
      }

      openZoom(index) {
        if (!this.overlay) return
        if (this._closeTimer) {
          clearTimeout(this._closeTimer)
          this._closeTimer = null
        }
        // Pause the inline video (it will be hidden behind the overlay).
        this.track.querySelectorAll('video').forEach((v) => {
          try {
            v.pause()
          } catch (e) {}
        })
        // Show + build + scroll in one synchronous pass, then lock body scroll
        // and fade in over the following two frames.
        this.overlay.classList.remove('hidden')
        this.overlay.classList.add('flex')
        this._buildOverlay() // no-op when idle prebuild already ran
        this._overlayScrollToIndex(index, false)
        requestAnimationFrame(() => {
          document.body.style.overflow = 'hidden'
          requestAnimationFrame(() => {
            this.overlay.classList.add('is-open')
          })
        })
      }

      closeZoom() {
        if (!this.overlay) return
        document.body.style.overflow = ''
        // Pause any overlay video the user was watching.
        if (this.overlayTrack)
          this.overlayTrack.querySelectorAll('video').forEach((v) => {
            try {
              v.pause()
            } catch (e) {}
          })
        this.overlay.classList.remove('is-open')
        if (this._closeTimer) clearTimeout(this._closeTimer)
        this._closeTimer = setTimeout(() => {
          this._closeTimer = null
          this.overlay.classList.add('hidden')
          this.overlay.classList.remove('flex')
          // Resume the active inline slide's video once the overlay is gone.
          this._syncSlideVideo(this.activeIndex)
        }, 200)
      }

      // Mark the active image high-priority; demote neighbours and others.
      // Big overlay images are already eager (set at build time), so we only
      // manage fetchpriority here — changing img.loading after insertion has
      // no effect in iOS Safari.
      _loadAround(i) {
        if (!this.overlayTrack) return
        const slides = Array.from(this.overlayTrack.children)
        slides.forEach((slide, si) => {
          const img = slide.querySelector('img')
          if (!img) return
          if (si === i) {
            img.setAttribute('fetchpriority', 'high')
          } else {
            img.removeAttribute('fetchpriority')
          }
        })
      }

      // Build the overlay's slides + thumbs from the currently visible main slides.
      _buildOverlay() {
        if (!this.overlay || !this.overlayTrack) return
        const slides = this.visibleSlides()
        const sig = slides
          .map((s) => s.getAttribute('data-media-id') || '')
          .join(',')
        if (sig === this._overlaySig && this.overlayTrack.children.length) return
        this._overlaySig = sig
        this.overlayTrack.innerHTML = ''
        if (this.overlayThumbs) this.overlayThumbs.innerHTML = ''
        this._overlayVideoIndex = -1

        slides.forEach((slide, i) => {
          const isVideo = slide.getAttribute('data-media-type') === 'video'
          if (isVideo && this._overlayVideoIndex < 0) this._overlayVideoIndex = i

          // The inline slide always has an <img> — either the product photo or
          // the video preview image. Use it as the poster and thumb source.
          const slideImg = slide.querySelector('img')
          const posterSrc = slideImg
            ? slideImg.currentSrc || slideImg.getAttribute('src') || ''
            : ''

          // ── Big slide ──────────────────────────────────────────────────
          const big = document.createElement('div')
          big.className = 'mpg-ov-slide'

          if (isVideo) {
            const videoSrc = slide.getAttribute('data-video-src')
            if (videoSrc) {
              const video = document.createElement('video')
              video.setAttribute('controls', '')
              video.setAttribute('preload', 'metadata')
              video.setAttribute('playsinline', '')
              // Start muted like the inline gallery; the user can unmute via
              // the native controls if they want sound.
              video.muted = true
              video.setAttribute('muted', '')
              video.classList.add('mpg-ov-media')
              if (posterSrc) video.setAttribute('poster', posterSrc)
              const source = document.createElement('source')
              source.src = videoSrc
              source.type = 'video/mp4'
              video.appendChild(source)
              big.appendChild(video)
            }
          } else if (slideImg) {
            const clone = document.createElement('img')
            if (slideImg.srcset) clone.srcset = slideImg.srcset
            clone.src = slideImg.getAttribute('src') || ''
            clone.alt = slideImg.alt || ''
            // Do NOT set loading="lazy" here. These images share the same URL
            // as the inline gallery, so the browser serves them from cache once
            // the inline images have loaded. Lazy would cause iOS Safari to
            // ignore the fetchpriority upgrade in _loadAround (changing the
            // loading attribute after insertion has no effect on iOS).
            clone.setAttribute('decoding', 'async')
            clone.classList.add('mpg-ov-media')
            big.appendChild(clone)
          }
          this.overlayTrack.appendChild(big)

          // ── Thumbnail ──────────────────────────────────────────────────
          if (this.overlayThumbs) {
            const thumb = document.createElement('button')
            thumb.type = 'button'
            thumb.className = 'mpg-ov-thumb'
            thumb.setAttribute('aria-label', 'View image ' + (i + 1))
            const timg = document.createElement('img')
            timg.setAttribute('loading', 'lazy')
            timg.setAttribute('decoding', 'async')
            timg.alt = slideImg ? slideImg.alt || '' : ''
            timg.src = posterSrc
            if (!isVideo && slideImg && slideImg.srcset) {
              timg.srcset = slideImg.srcset
              timg.setAttribute('sizes', '120px')
            }
            thumb.appendChild(timg)
            thumb.addEventListener('click', () =>
              this._overlayScrollToIndex(i, true)
            )
            this.overlayThumbs.appendChild(thumb)
          }
        })

        if (this.overlayVideoBtn)
          this.overlayVideoBtn.classList.toggle(
            'hidden',
            this._overlayVideoIndex < 0
          )

        const multi = slides.length > 1
        ;[this.overlayPrev, this.overlayNext].forEach(
          (b) => b && b.classList.toggle('hidden', !multi)
        )
      }

      _overlayScrollToIndex(i, smooth) {
        if (!this.overlayTrack) return
        const slides = Array.from(this.overlayTrack.children)
        if (!slides[i]) return
        this._overlaySuppressSync = true
        if (this._ovSuppressTimer) clearTimeout(this._ovSuppressTimer)
        this._ovSuppressTimer = setTimeout(
          () => {
            this._overlaySuppressSync = false
          },
          smooth ? 450 : 80
        )
        this.overlayTrack.scrollTo({
          left: slides[i].offsetLeft,
          behavior: smooth ? 'smooth' : 'auto',
        })
        this._loadAround(i)
        this._overlaySetActiveThumb(i)
      }

      _overlaySyncFromScroll() {
        if (this._overlaySuppressSync) return
        if (!this.overlayTrack) return
        const slides = Array.from(this.overlayTrack.children)
        if (!slides.length) return
        const center =
          this.overlayTrack.scrollLeft + this.overlayTrack.clientWidth / 2
        let best = 0
        let bestDist = Infinity
        slides.forEach((s, i) => {
          const c = s.offsetLeft + s.offsetWidth / 2
          const d = Math.abs(center - c)
          if (d < bestDist) {
            bestDist = d
            best = i
          }
        })
        this._overlaySetActiveThumb(best)
      }

      _overlayStep(dir) {
        if (!this.overlayTrack) return
        const count = this.overlayTrack.children.length
        if (!count) return
        let i = (this._overlayIndex || 0) + dir
        if (i < 0) i = count - 1
        if (i >= count) i = 0
        this._overlayScrollToIndex(i, true)
      }

      _overlaySetActiveThumb(i) {
        this._overlayIndex = i
        if (!this.overlayThumbs) return
        Array.from(this.overlayThumbs.children).forEach((t, ti) =>
          t.classList.toggle('is-active', ti === i)
        )
        const active = this.overlayThumbs.children[i]
        if (active && active.scrollIntoView)
          active.scrollIntoView({
            inline: 'center',
            block: 'nearest',
            behavior: 'smooth',
          })
      }
    }
  )
}
